import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg shadow-sm border-b border-gray-100">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="w-16 h-16 transition-transform duration-300 group-hover:scale-110">
              <img 
                src="https://customer-assets.emergentagent.com/job_pride-support/artifacts/0llcl2qk_lions%20logo.png" 
                alt="Lions Unleashed Logo" 
                className="w-full h-full object-contain animate-logo"
              />
            </div>
            <div className="ml-3">
              <div className="text-xl font-bold" style={{ color: '#D4AF37' }}>LIONS UNLEASHED</div>
              <div className="text-xs text-gray-600">Unlocking your future career</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <div className="relative group">
              <button className="text-gray-700 hover:text-cyan-500 font-medium flex items-center transition-colors">
                About Us <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <Link to="/mission" className="block px-4 py-3 hover:bg-gray-50 text-gray-700 hover:text-cyan-500 rounded-t-lg">
                  Purpose & Mission
                </Link>
                <Link to="/team" className="block px-4 py-3 hover:bg-gray-50 text-gray-700 hover:text-cyan-500">
                  Our Team
                </Link>
                <Link to="/mental-health-team" className="block px-4 py-3 hover:bg-gray-50 text-gray-700 hover:text-cyan-500 pl-8 text-sm">
                  Mental Health Team
                </Link>
                <Link to="/donors" className="block px-4 py-3 hover:bg-gray-50 text-gray-700 hover:text-cyan-500 rounded-b-lg">
                  Our Donors
                </Link>
              </div>
            </div>
            
            <div className="relative group">
              <button className="text-gray-700 hover:text-cyan-500 font-medium flex items-center transition-colors">
                Why Join Our Program? <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-48 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link to="/benefits" className="block px-4 py-3 hover:bg-gray-50 text-gray-700 hover:text-cyan-500">
                  Program Benefits
                </Link>
                <Link to="/success-stories" className="block px-4 py-3 hover:bg-gray-50 text-gray-700 hover:text-cyan-500">
                  Success Stories
                </Link>
              </div>
            </div>

            {/* Blog link temporarily hidden
            <Link 
              to="/blog" 
              className={`text-gray-700 hover:text-cyan-500 font-medium transition-colors ${isActive('/blog') ? 'text-cyan-500' : ''}`}
            >
              Blog
            </Link>
            */}

            <Link 
              to="/register" 
              className={`text-gray-700 hover:text-cyan-500 font-medium transition-colors ${isActive('/register') ? 'text-cyan-500' : ''}`}
            >
              Register
            </Link>

            <Link 
              to="/contact" 
              className={`text-gray-700 hover:text-cyan-500 font-medium transition-colors ${isActive('/contact') ? 'text-cyan-500' : ''}`}
            >
              Contact
            </Link>

            <Link to="/donate">
              <Button 
                className="font-semibold text-black hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)' }}
              >
                Donate →
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="container mx-auto px-4 py-4 space-y-4">
            <Link to="/mission" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Purpose & Mission
            </Link>
            <Link to="/team" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Our Team
            </Link>
            <Link to="/mental-health-team" className="block py-2 pl-4 text-gray-600 hover:text-cyan-500 text-sm" onClick={() => setMobileMenuOpen(false)}>
              Mental Health Team
            </Link>
            <Link to="/donors" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Our Donors
            </Link>
            <Link to="/benefits" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Program Benefits
            </Link>
            <Link to="/success-stories" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Success Stories
            </Link>
            {/* Blog link temporarily hidden
            <Link to="/blog" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Blog
            </Link>
            */}
            <Link to="/register" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Register
            </Link>
            <Link to="/contact" className="block py-2 text-gray-700 hover:text-cyan-500" onClick={() => setMobileMenuOpen(false)}>
              Contact
            </Link>
            <Link to="/donate" className="block" onClick={() => setMobileMenuOpen(false)}>
              <Button 
                className="w-full font-semibold text-black"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)' }}
              >
                Donate →
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;